import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect } from 'react';
import { VStack } from '@/components/Layout';
import { usePackage } from './-hooks/usePackage';
import { listOperationPackages, listPackagePatterns } from './-query/api';
import { fetchDeviceInventoryPatternGenerations, shouldSortDeviceInventoryPatterns } from './-query/fetchDeviceInventoryPatternGenerations';
import { sortDeviceInventoryPatterns } from './-query/sortDeviceInventoryPatterns';
import { SearchSchema } from './-zod-schemas';

/**
 * IT Operation Coreのパッケージの詳細な表示を行うコンポーネント。
 * サイドバー以外のDataTable部分を表示することを主な責務とする。
 * 個別の packageType ごとにディレクトリを分ける手もあったが、Operation Package自体が汎用的な機構であるため、
 * できる限りその設計思想に乗っ取るようにした。
 */
export const Route = createFileRoute('/_staff/it-operation-core/_root/packages/$packageId')({
  loader: async ({
    params,
    context
  }) => {
    // パッケージパターンと全パッケージリストの両方を取得
    const [packagePatterns, allPackages] = await Promise.all([listPackagePatterns(params.packageId), listOperationPackages()]);

    // 棚卸しだけ v2 パターンを先頭へ寄せる。この並びはタブの左端だけでなく
    // 既定 patternId（下の useEffect が `packagePatterns[0]` を選ぶ）にも効くため、
    // ここで解かないと v1 パターンが初期表示されうる。
    // この loader は全パッケージ種別が通る共通経路なので、棚卸し以外では
    // pattern-generations を一切呼ばない（`shouldSortDeviceInventoryPatterns`）。
    const packageType = Object.values(allPackages).flat().find(p => p.id === params.packageId)?.type;
    const sortedPackagePatterns = shouldSortDeviceInventoryPatterns(packageType) ? sortDeviceInventoryPatterns(packagePatterns, await fetchDeviceInventoryPatternGenerations(context.queryClient)) : packagePatterns;
    return {
      packagePatterns: sortedPackagePatterns,
      allPackages,
      packageId: params.packageId
    };
  },
  validateSearch: zodValidator(SearchSchema),
  component: RouteComponent
});
function RouteComponent() {
  const navigate = useNavigate();
  const {
    packageId
  } = Route.useParams();
  const search = Route.useSearch();
  const {
    patternId
  } = search;
  const {
    packagePatterns,
    allPackages
  } = Route.useLoaderData();

  // ローダーで取得したデータから該当パッケージを検索
  const operationPackage = Object.values(allPackages).flat().find(p => p.id === packageId);
  if (!operationPackage) {
    throw new Error(`Operation package not found for ID: ${packageId}`);
  }

  // PackageTypeごとに表示するコンポーネントを切り替える
  const {
    Component
  } = usePackage({
    packageType: operationPackage.type
  });
  useEffect(() => {
    if (!patternId && packagePatterns.length > 0) {
      navigate({
        to: '/it-operation-core/packages/$packageId',
        params: {
          packageId
        },
        search: {
          ...search,
          patternId: packagePatterns[0].operationPackagePatternId
        },
        // デフォルト patternId の補完は履歴を置換する（push すると patternId 無しの
        // 中間エントリが残り、ブラウザバックで遷移元に戻れなくなる）
        replace: true
      });
    }
  }, [packagePatterns, patternId, navigate, packageId, search]);
  return <VStack className="h-full w-full">
      <Component operationPackage={operationPackage} packagePatterns={packagePatterns} patternId={patternId} searchSchema={search} />
    </VStack>;
}